import { fireEvent, render, waitFor } from '@testing-library/preact';
import { expect } from 'chai';
import { VNode } from 'preact';

import { Metadata } from '../../../UNSAFE_Collection';
import userEvent from '@testing-library/user-event';

import { populateData } from './listViewTestFixtures';
import type { Employee } from './listViewTestFixtures';
import { getRoot } from './testHelpers';

export type Props<K> = {
  data: { data: Employee<K>; metadata: Metadata<K> }[];
};

type TestComponentProps<K extends string | number> = (props: Props<K>) => VNode;

export const testTabbableMode = (TestComponent: TestComponentProps<any>) => {
  it('acts as single tab stop in composition', async () => {
    const data = populateData(100, true) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata} />);
    const grid = await getRoot(list);

    userEvent.tab();
    expect(document.activeElement).equals(list.getByText('Preceding Button'));
    userEvent.tab();
    expect(document.activeElement).equals(grid);
    userEvent.tab();
    expect(document.activeElement).equals(list.getByText('Following Button'));
    userEvent.tab({ shift: true });
    expect(document.activeElement).equals(grid);
    userEvent.tab({ shift: true });
    expect(document.activeElement).equals(list.getByText('Preceding Button'));
  });

  it('enters interactive mode with keyboard via F2', async () => {
    const data = populateData(100, true) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata} />);
    const grid = await getRoot(list);

    // Tab past first button into ListView
    userEvent.tab();
    userEvent.tab();
    expect(document.activeElement).equals(grid);

    list.rerender(<TestComponent data={dataMetadata}></TestComponent>);
    const firstRowInput = list.getByTestId('input-0');
    expect(firstRowInput.getAttribute('tabindex')).equals('-1');
    fireEvent.keyDown(grid, { key: 'F2' });
    expect(firstRowInput.getAttribute('tabindex')).equals('0');
  });

  it('enters and leaves interactive mode with keyboard via F2, ESC and arrow keys', async () => {
    const data = populateData(100, true) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata} />);
    const grid = await getRoot(list);

    // Tab past first button into ListView
    userEvent.tab();
    userEvent.tab();
    expect(document.activeElement).equals(grid);

    list.rerender(<TestComponent data={dataMetadata}></TestComponent>);
    const firstRowInput = list.getByTestId('input-0');
    expect(firstRowInput.getAttribute('tabindex')).equals('-1');
    fireEvent.keyDown(grid, { key: 'F2' });
    expect(firstRowInput.getAttribute('tabindex')).equals('0');
    fireEvent.keyDown(grid, { key: 'Esc' });
    await waitFor(() => {
      expect(document.activeElement).equals(grid);
    });
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    const secondRowInput = list.getByTestId('input-1');
    expect(secondRowInput.getAttribute('tabindex')).equals('-1');
    fireEvent.keyDown(grid, { key: 'F2' });
    expect(secondRowInput.getAttribute('tabindex')).equals('0');
  }, 3000);

  it('enters interactive mode with click on input', async () => {
    const data = populateData(100, true) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata} />);

    const firstRowInput = list.getByTestId('input-0');
    expect(firstRowInput.getAttribute('tabindex')).equals('-1');
    userEvent.click(firstRowInput);
    // NOTE without this rerender the state change doesn't stick
    list.rerender(<TestComponent data={dataMetadata}></TestComponent>);
    expect(firstRowInput.getAttribute('tabindex')).equals('0');
    expect(document.activeElement).equals(firstRowInput);
    userEvent.tab();
    expect(document.activeElement).equals(list.getByTestId('button-0'));
  });

  // FIXME: reenable this test once  is fixed
  xit('leaves interactive mode with click outside', async () => {
    const data = populateData(100, true) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(<TestComponent data={dataMetadata} />);

    const firstRowInput = list.getByTestId('input-0');
    expect(firstRowInput.getAttribute('tabindex')).equals('-1');
    userEvent.click(firstRowInput);
    // NOTE without this rerender the state change doesn't stick
    list.rerender(<TestComponent data={dataMetadata} />);
    expect(firstRowInput.getAttribute('tabindex')).equals('0');
    expect(document.activeElement).equals(firstRowInput);
    const outsideElement = list.getByText('Preceding Button');
    userEvent.click(outsideElement);
    expect(document.activeElement).equals(outsideElement);
    list.rerender(<TestComponent data={dataMetadata} />);
    expect(firstRowInput.getAttribute('tabindex')).equals('-1');
  });
};
